import { EventTypes } from 'redux-segment';

import postPin from 'actions/pin/postPin.action';
import setCurrentSalon from 'actions/salon/setCurrentSalon.action';
import CONF from 'config/conf.singleton';
import FLAG from 'config/flag.singleton';
import PUSHER from 'config/pusher.singleton';
import QUERY from 'config/query.singleton';
import quickfix from 'config/quickfix.singleton';
import RELEASE from 'config/release.singleton';
import SCAN from 'config/scan.singleton';
import SEGMENT from 'config/segment.singleton';
import SENTRY from 'config/sentry.singleton';
import UMSG from 'config/umsg.singleton';
import LINK from 'constants/link.const';
import { IGNORE } from 'constants/misc.const';
import endpoint from 'utils/backend/endpoint.util';
import { xeLogoutUser } from 'utils/cognito.util';
import dispatch from 'utils/core/dispatch.util';
import getState from 'utils/core/getState.util';
import findFrame from 'utils/dom/findFrame.util';
import messageWindow from 'utils/dom/messageWindow.util';
import { isEnvProd } from 'utils/env/determine.util';
import linkTo from 'utils/links/linkTo.util';
import actionBe from 'utils/mw/actionBe.util';
import actionEndpointBe from 'utils/mw/actionEndpointBe.util';

import { SET_CURRENT_SALON } from 'salon/constants';

const CTAG = '[SLICK]';
const CTAG2 = '[INIT-RELATED]';

const logW = /**@type VariadicV*/ console.warn.bind(console, '[W]', CTAG, CTAG2); // eslint-disable-line no-console,no-unused-vars
const logD = /**@type VariadicV*/ console.log.bind(console, '[D]', CTAG, CTAG2); // eslint-disable-line no-console,no-unused-vars

const IS_PROD = isEnvProd();

// NOTE: short name for restricted, used often, don't want to take up space
// the fields will be removed in production due to possible unwanted side effects and/or security risks
const R = ($) => (IS_PROD ? IGNORE : $);

// this object will be the singleton due to it being defined at module level and exported
const SLICK = {
  get state() {
    if ('function' === typeof SLICK.stat) return SLICK.stat();
    if (!IS_PROD) logW('get state()', 'Invalid SLICK.stat() function');
    return void 1;
  },
};

// empty modifiable object, for convenience of flags/features transition
// SEE: https://getslick.atlassian.net/browse/GS-10404
SLICK.makeshiftStore = {};

// objects
SLICK.conf = R(CONF);
SLICK.flag = FLAG;
SLICK.pusher = R(PUSHER);
SLICK.qc = R(QUERY);
SLICK.release = RELEASE;
SLICK.root = document.getElementById('root');
SLICK.scan = R(SCAN);
SLICK.segment = R(SEGMENT);
SLICK.sentry = R(SENTRY);
SLICK.umsg = UMSG;

// functions
SLICK.messageWindow = R(messageWindow);
SLICK.findFrame = findFrame;
SLICK.logout = () => {
  const action = xeLogoutUser(dispatch, getState);
  logD('logout()', 'action:', action);
  return dispatch(action);
};
SLICK.pinup = (...args) => dispatch(postPin(...args));
SLICK.salonM = ({ salonId, isSupport }) => dispatch(setCurrentSalon({ salonId, isSupport, userRole: 'manager' }));
SLICK.salonR = ({ salonId, isSupport }) => dispatch(setCurrentSalon({ salonId, isSupport, userRole: 'receptionist' }));

SLICK.pickSalon = () => {
  dispatch({ type: SET_CURRENT_SALON, salon: null, meta: { analytics: EventTypes.track } });
  window.location = linkTo({ pattern: '/#/salon', oldPattern: LINK.salonPick });
  return window.location;
};

SLICK.quickfix = quickfix;

// redux
SLICK.stat = R(getState);
SLICK.disp = R((type, action) => {
  const t = String(type ?? '').trim();
  return t ? dispatch({ ...(action ?? {}), type: t }) : void 1;
});
SLICK.be1 = R((...args) => dispatch(endpoint(...args)));
SLICK.be2 = R((atag, method, path, config, ...args) =>
  dispatch(
    actionBe(atag, ({ AT, ATAG }) =>
      endpoint(method, path, {
        AT,
        name: ATAG,
        ...config,
      }),
    )(...args),
  ),
);
SLICK.be3 = R((atag, method, pattern, config, ...args) => dispatch(actionEndpointBe(atag, method, pattern, (options, extra) => ({ ...options, ...extra }))(...args)));

// the code that actually deletes properties marked with the symbol IGNORE (to avoid using the value undefined)
for (const [key, val] of Object.entries(SLICK)) if (val === IGNORE) delete SLICK[key];

// noinspection JSUnusedGlobalSymbols
export default SLICK;
